import { useQueries } from '@tanstack/react-query';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';

import {
  fetchShopifyProduct,
  SHOPIFY_PRODUCT_QUERY,
} from './useShopifyProduct';

interface UseFullShopifyProductsResult {
  data: (ShopifyProduct | undefined)[];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  errors: unknown[];
  refetchAll: () => void;
}

export const useFullShopifyProducts = (
  handles: string[],
): UseFullShopifyProductsResult => {
  const {
    value: { countryCode },
    isLoading: isLocaleLoading,
  } = useLocalStorage(UserLocale);

  const queries = useQueries({
    queries: handles.map((handle) => ({
      queryKey: [SHOPIFY_PRODUCT_QUERY, handle, countryCode] as [
        string,
        string,
        string,
      ],
      queryFn: fetchShopifyProduct,
      enabled: !isLocaleLoading && Boolean(handle),
    })),
  });

  const data = queries.map((q) => q.data);

  const isLoading = isLocaleLoading || queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isSuccess =
    !isLoading &&
    !isError &&
    (handles?.length || 0) > 0 &&
    queries.every((q) => q.isSuccess);
  const errors = queries.map((q) => q.error).filter(Boolean) as unknown[];

  const refetchAll = () => {
    queries.forEach((q) => q.refetch());
  };

  return { data, isLoading, isError, isSuccess, errors, refetchAll };
};
